/**
 * dood-front formatting (colors, images) after bound-value resolution.
 * Structural port of dood-preview.adapter.js — without $param / $cases resolution.
 */

const COLOR_PROP_SUFFIXES = ['_color', '_colour'];
const COLOR_PROP_EXACT = new Set([
  'hamburger_color',
  'background_color',
  'text_color',
  'label_color',
  'icon_color',
  'icon_background_color',
  'button_text_color',
  'button_background_color',
  'shop_search_background_color',
  'shop_search_text_color',
  'shop_search_placeholder_color',
  'shop_search_icon_color',
  'shop_search_border_color',
]);

const IMAGE_PROP_KEYS = new Set(['logo', 'background_image', 'backgroundImage', 'src', 'image']);

const PALETTE_TOKEN_RE = /^(primary|neutral|secondary|error|warning|success)(-\d+)?$/;

export function transformResolvedPropTree(value: unknown, key = '', assetsBaseUrl = ''): unknown {
  if (isColorPropKey(key)) {
    return toDoodColor(value);
  }
  if (
    IMAGE_PROP_KEYS.has(key) ||
    key.endsWith('_image') ||
    key.endsWith('Image') ||
    key.endsWith('_video') ||
    key.endsWith('Video')
  ) {
    return toDoodImage(value, assetsBaseUrl);
  }
  if (Array.isArray(value)) {
    return value.map(item =>
      typeof item === 'object' && item !== null
        ? transformResolvedPropTree(item, key, assetsBaseUrl)
        : item,
    );
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = transformResolvedPropTree(v, k, assetsBaseUrl);
    }
    return out;
  }
  return value;
}

export function toDoodColor(raw: unknown): unknown {
  if (raw === null || raw === undefined || raw === '') return undefined;

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    if ('type' in obj && 'value' in obj) {
      const inner = obj['value'];
      if (inner !== null && typeof inner === 'object' && !Array.isArray(inner)) {
        const nestedColor = toDoodColor(inner);
        if (nestedColor !== undefined) {
          return nestedColor;
        }
      }
      if (typeof obj['value'] === 'string') {
        return { type: String(obj['type']).toLowerCase(), value: String(obj['value']) };
      }
    }
    if (typeof obj['value'] === 'string') {
      return toDoodColor(obj['value']);
    }
  }

  const str = String(raw).trim();
  if (!str) return undefined;

  if (str.startsWith('#') || str === 'transparent' || /^rgb/i.test(str) || /^hsl/i.test(str)) {
    return { type: 'hex', value: str };
  }

  if (PALETTE_TOKEN_RE.test(str)) {
    return { type: 'palette', value: str };
  }

  if (/^(primary|neutral|secondary|error|warning|success)/.test(str)) {
    return { type: 'palette', value: str };
  }

  return { type: 'hex', value: str };
}

export function toDoodImage(raw: unknown, assetsBaseUrl: string): unknown {
  if (raw === null || raw === undefined) return undefined;

  if (typeof raw === 'string') {
    const url = prefixAssetUrl(raw.trim(), assetsBaseUrl);
    return url ? { url } : undefined;
  }

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = { ...(raw as Record<string, unknown>) };
    const src = obj['url'] ?? obj['src'];
    if (typeof src === 'string') {
      const url = prefixAssetUrl(src.trim(), assetsBaseUrl);
      if (url) {
        obj['url'] = url;
        delete obj['src'];
      }
    }
    return obj;
  }

  return raw;
}

function prefixAssetUrl(url: string, assetsBaseUrl: string): string | null {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  if (url.startsWith('/')) {
    return assetsBaseUrl ? `${assetsBaseUrl.replace(/\/$/, '')}${url}` : url;
  }
  return url;
}

function isColorPropKey(key: string): boolean {
  const snake = camelToSnake(key);
  if (COLOR_PROP_EXACT.has(snake)) return true;
  return COLOR_PROP_SUFFIXES.some(s => snake.endsWith(s));
}

function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, c => `_${c.toLowerCase()}`);
}

const COLOR_UI_SKIP_KEYS = new Set(['input', 'overrides']);

export function mapColorsParam(raw: unknown): Record<string, Record<string, string>> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const result: Record<string, Record<string, string>> = {};
  for (const [key, val] of Object.entries(raw as Record<string, unknown>)) {
    if (COLOR_UI_SKIP_KEYS.has(key)) continue;
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      result[key] = val as Record<string, string>;
      continue;
    }
    if (typeof val === 'string' && val.trim()) {
      result[key] = { '600': val.trim() };
    }
  }

  return Object.keys(result).length > 0 ? result : null;
}

export function normalizeCustomCss(raw: unknown): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string' && raw.trim()) return [raw.trim()];
  return [];
}
